import { createFilterOptions, FilterOptionsState, SelectOption } from '@chronosphereio/chrono-ui';
import { useCallback, useMemo, useRef } from 'react';

interface ExtraOption extends SelectOption {
  isExtra: boolean;
}

const EXTRA_OPTION_DEFAULT_KEY = '__EXTRA_OPTION_DEFAULT_KEY__';

export const useExtraOption = (options: SelectOption[], selected: SelectOption[] = []) => {
  const baseFilterOptions = useMemo(() => {
    return createFilterOptions<ExtraOption>();
  }, []);

  // MUI has issues with autohighlight when items are being added/removed from the list of options
  // To get around this, we are going to add this option to the list, and just update it's key/value
  // on typing, and use an extra boolean to hide it when we need to
  const extraOptionRef = useRef<ExtraOption>({
    key: EXTRA_OPTION_DEFAULT_KEY,
    label: '', // Empty string so the off chance this shows up in the dropdown, it's not confusing
    isExtra: true,
  });

  const setExtraOption = useCallback((value: string) => {
    if (value === '') {
      extraOptionRef.current.label = '';
      extraOptionRef.current.key = EXTRA_OPTION_DEFAULT_KEY;
    } else {
      extraOptionRef.current.label = value;
      extraOptionRef.current.key = value;
    }
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setExtraOption(e.target.value);
    },
    [setExtraOption]
  );

  // If a value is selected, but for some reason it's not in the options, we need to make sure it's included
  const optionsWithSelected = useMemo(() => {
    const selectedButNotInOptions = selected.filter((s) => !options.some((o) => o.key === s.key));
    return [...options, ...selectedButNotInOptions];
  }, [options, selected]);

  const optionsWithExtra = useMemo(() => {
    if (extraOptionRef.current) {
      return [...optionsWithSelected, extraOptionRef.current];
    }

    return optionsWithSelected;
  }, [optionsWithSelected]);

  // Custom filter options fn that will always filter out the custom extra option until the user types something
  // and there are only a few options left
  const filterOptions = useMemo(() => {
    return (unCastOptions: SelectOption[], state: FilterOptionsState<SelectOption>) => {
      const options = unCastOptions as ExtraOption[];
      const allFilteredOptions = baseFilterOptions(options, state);

      // We only want to include the extra option if they've typed in something or there are only a couple of options showing
      if (state.inputValue === '' || allFilteredOptions.length > 5) {
        return allFilteredOptions.filter((o) => o.isExtra !== true);
      }

      return allFilteredOptions;
    };
  }, [baseFilterOptions]);

  return {
    filterOptions: filterOptions,
    extraOption: extraOptionRef.current,
    setExtraOption,
    onInputChangeHandler: handleInputChange,
    optionsWithExtra,
  };
};
